import { IssueStatusName } from '@helios/ag-grid-config'
import { BreakCategory, Command, IssueGroup } from '@helios/state-management'

export type DataSourceType = 'CUSTODY' | 'TRADING'
export enum BreakPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
}

export interface Break<
  T extends Record<string, unknown> = Record<string, unknown>,
> {
  acctId: number
  identifier: string
  sourceName: string
  typeName: string
  categoryName: BreakCategory
  categoryPriority: number
  displayName: string | IssueGroup
  statusName: IssueStatusName
  issueGroup: string
  priority: BreakPriority
  securityId: number
  securityIdentifier: string
  securityCusip: string
  securityIsin: string
  securityFigi: string
  securitySedol: string
  securityType?: string
  securityName?: string
  note?: string
  actionCode?: string
  descriptionCode?: string
  message: string
  startDate: Date | string
  endDate: Date | string
  createdDateTime: Date | string
  expirationDate: Date | string
  dataSourceTypes: DataSourceType[]
  dataSourceName?: string
  metaData: T
  availableActions: string[]
  actionLimits: {
    holdLimitInDays: number
  }
  ultimateParentClientId?: number
  tranDate: Date | string
  reconInsightIssueId?: number
  reconInsightIssueStatus?: string
  cwanIdentifier?: string
  restrictedAction?: string
  accountName?: string
  custodyAccountNumber?: string
}

export interface BreaksAdvancedOptions {
  showResolvedBreaks: boolean
}

export interface CWANValueMetaData {
  'CWAN Value': string
}

export interface PredictedValueMetaData {
  'Predicted Value': string
}

export interface DifferenceMetaData {
  'Day Difference': string
  difference: string
}

export type dataSourceTypes = 'CUSTODY' | 'TRADING'
export type statusNamesTypes = 'IGNORED' | 'FOUND' | 'WAITING'

export interface DownloadForm {
  primary: {
    accounts: number[]
    clients: number[]
    parentClients: number[]
    securityIds: number[]
  }
  secondary: {
    breakTypes: string[]
    breakStatus: string[]
    breakPriority: string[]
  }
}

export interface FindBreaksForm {
  rootType: string
  rootIds: number[]
  searchIdType: string
  searchByIds: number[] | string[]
  breakStatus: string[]
  breakPriority: string[]
  breakTypes: string[]
  breakIds: string[]
  breakCategories: string[]
}

export interface BreakResponse {
  data: Break[]
  matchingBreaksCount: string
}

export interface BreakValidationResult {
  breakId: string
  successful: boolean
  commands: Command[]
  messages: string[]
}

export interface BreakResponseObject {
  commands: Command[]
  breakValidationResults: BreakValidationResult[]
}

export enum BreakNoteHandling {
  REPLACE = 'Replace',
  APPEND = 'Append',
}

export interface BreakType {
  sourceName: string
  breakGroup: string
  displayName: string
}

export interface Category {
  category: string
  name: string
}

export interface BreakSetValues {
  status: IssueStatusName[]
  breakType: BreakType[]
  priority: BreakPriority[]
  categories: Category[]
}

export interface BreakHistory {
  statusName: IssueStatusName
  toolName: string
  dateTime: Date
  userId: number
  userName: string
  expiration?: {
    statusName: IssueStatusName
    date: string
  }
  note: string
  researchNote: string
  metaData: string
  actionCode?: string
  descriptionCode?: string
}

export interface ExportBreaksToExcel {
  filter: PopOverFilter
  headersMap: { [key: string]: string }
  userTimeZone: string
}

export type BreakCodeResponse = Record<number, AccountBreakCodes>

export interface AccountBreakCodes {
  ACTION?: BreakCode[]
  DESCRIPTION?: BreakCode[]
}

export interface BreakCode {
  clientId?: number
  code: string
  breakTypes: string[]
  codeType: BreakCodeType
  id: string
  isActive?: boolean
  lastModifiedAt?: string
  userName: string
  notesRequired?: boolean
}

export type BreakCodeType = 'ACTION' | 'DESCRIPTION'

/**
 * A copied break's codes + note, as held by BreakCodeClipboardService.
 *
 * `null` codes and an empty note are MEANINGFUL, not "unset": paste is a strict mirror
 * of the source break (PVAL-26114), so a field the source did not have clears the
 * corresponding box in the target dialog. Lives here rather than beside the service so
 * model/ never imports from services/ (the epic layers model ← validators ← components).
 */
export interface BreakCodeClipboardEntry {
  actionCode: string | null
  descriptionCode: string | null
  note: string
}

/**
 * Result of matching the configured break codes against the selected break(s).
 * - `unavailable`: the /accounts/active response is missing (fetch failed / not loaded).
 * - `mixedCodeSets`: the breaks span more than one code set (e.g. Enfusion + global) and
 *   cannot share codes. Unreachable for a single break; defined for the shared/bulk caller.
 * - `ready`: codes resolved for this break type (either array may be empty = none configured).
 */
export type CodeAvailability =
  | { status: 'unavailable' }
  | { status: 'mixedCodeSets' }
  | { status: 'ready'; actionCodes: BreakCode[]; descriptionCodes: BreakCode[] }

/**
 * The Enfusion code set is the one owned by the Enfusion ultimate parent client;
 * every other client shares the single global code set. Mirrors the backend
 * constant CiamResourceFactory.ENFUSION_ULTIMATE_CLIENT_ID.
 */
export const ENFUSION_ULTIMATE_PARENT_CLIENT_ID = 53938

function isEnfusionBreak(b: Break): boolean {
  return b.ultimateParentClientId === ENFUSION_ULTIMATE_PARENT_CLIENT_ID
}

/**
 * True when the selection spans BOTH code sets — i.e. it mixes Enfusion and
 * non-Enfusion (global) breaks, which cannot be coded together. Any number of
 * clients on the same side (all global, or all Enfusion) share one code set and
 * are compatible.
 */
export function spansMultipleCodeSets(breaks: Break[]): boolean {
  return new Set(breaks.map(isEnfusionBreak)).size > 1
}

export function resolveBreakCodes(
  breaks: Break[],
  breakCodes: BreakCodeResponse | undefined,
): CodeAvailability {
  if (!breakCodes) {
    return { status: 'unavailable' }
  }
  if (breaks.length === 0) {
    return { status: 'ready', actionCodes: [], descriptionCodes: [] }
  }

  if (spansMultipleCodeSets(breaks)) {
    return { status: 'mixedCodeSets' }
  }

  const acctId = breaks[0].acctId
  const breakTypeArr = [
    ...new Set(
      breaks.map(b => (b.typeName ?? '').replace(/_(ERROR|FATAL)$/, '')),
    ),
  ]
  const matchesAllBreakTypes = (code: BreakCode): boolean =>
    breakTypeArr.every(bt => (code.breakTypes ?? []).includes(bt))

  const actionCodes = (breakCodes[acctId]?.ACTION ?? []).filter(
    matchesAllBreakTypes,
  )
  const descriptionCodes = (breakCodes[acctId]?.DESCRIPTION ?? []).filter(
    matchesAllBreakTypes,
  )
  return { status: 'ready', actionCodes, descriptionCodes }
}

export interface PopOverFilter {
  accountIds: number[]
  clientIds: number[]
  parentClientIds: number[]
  assetIds: string[] | number[]
  securityIds: string[] | number[]
  breakTypes: string[]
  breakStatuses: string[]
  breakPriorities: string[]
  breakIds: string[]
  breakCategories: string[]
}

export interface BreakAvailableRestrictedColumns {
  column: string
  isAvailable: boolean
}

export interface UserFeatureAccess {
  findBreaks: boolean
  findCurrentSessionBreaks: boolean
}
